import {t} from '@lingui/macro';
import _, {first, isString} from 'lodash';

import {isElectron} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {sanitizeMessageDraftHtml} from '../../../../srcCommon/helpers/messageDraftSanitizationHelpers';
import {EmailMessageBodyParts} from '../../../../srcCommon/models/messageHelpersTypes';
import {textToHtml} from '../../../../srcCommon/services/converters/textToHtmlConverter';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {EmailFormatsEnum} from '../../../helpers/models/emailFormatHelpers';
import {
  buildForwardQuote,
  buildReplyQuote,
  buildReplyQuoteWithHeaders,
  buildReplySubject,
} from '../../../helpers/models/messageHelpers';
import {buildMessagePatcherFactory} from '../../../helpers/models/messagePatchHelpers';
import {
  buildComposeRecipients,
  buildRecipientsFromVisual,
  buildReplyRecipients,
  findIntroducerRecipient,
  findRecipientForVariableInterpolation,
  getRecipientName,
  ReplyTypesEnum,
} from '../../../helpers/models/recipientHelpers';
import {selectSignaturesForChannel} from '../../../helpers/models/signatureHelpers';
import {NameParts, splitName} from '../../../helpers/nameSplitter';
import {batch} from '../../../helpers/reduxHelpers';
import {buildTwitterReplyText} from '../../../helpers/twitterHelpers';
import {
  AttachmentModel,
  isUploadedAttachmentModel,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {buildContactUid} from '../../../models/api/contactModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {
  DraftMessageModel,
  LoadedMessageModel,
  MessageModel,
  MessageStatusesEnum,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
  visualTypeFromType,
} from '../../../models/api/messageModel';
import {RecipientModel, RecipientRolesEnum} from '../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {findMessageComposer} from '../../../services/composers';
import {deletePrecog} from '../../../services/precogs';
import {deleteMessageTask} from '../../../services/tasks/deleteMessageTask';
import {InterfaceEventsEnum, ReplyLocationsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {saveMessageAsyncAction} from '../../../store/data/messages/saveMessageActions';
import {AppState} from '../../appState';
import {ThunkAction} from '../../appThunk';
import {getHasAutoShareDrafts} from '../../featureFlags/featureFlagsSelectors';
import {navigateComposerAction, popoutComposerAction} from '../../panels/composer/composerPanelActions';
import {makeGetIsPanelPopup} from '../../panels/panelsSelectors';
import {
  ComposerPanelId,
  EmlPanelId,
  MessagePanelId,
  RMessagePanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../panels/panelStateBase';
import {editDraftMessageAction} from '../../panels/viewer/viewerPanelActions';
import {getLocalize, Localizer} from '../../stateless/internationalizationSelectors';
import {fetchCardsForContactAsyncAction} from '../cards/cardsActions';
import {
  getChannels,
  getUserParticipatingChannels,
  makeGetAutoBccHandlesForChannelsFromRecipients,
  makeGetChannelToStealDraft,
  makeGetComposeChannel,
  makeGetReplyChannel,
  makeGetReplyChannels,
} from '../channels/channelsSelectors';
import {getCompany, getShouldReplyWithOutlookStyle} from '../company/companySelectors';
import {findMessageRecipients, makeGetVisualRecipients} from '../contacts/recipientsSelectors';
import {
  makeGetLoadedDraftMessages,
  makeGetUserDraftComposeMessages,
} from '../conversations/conversationsSelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getContacts, getConversations, getMessages, getOptimisticData} from '../dataSelectors';
import {DataState} from '../dataState';
import {
  getDefaultChannelId,
  getLastEmailFormat,
  getPreferences,
  getShouldReplyWithDefaultChannel,
} from '../preferences/preferencesSelectors';
import {getUser} from '../teammates/teammatesSelectors';
import {findDraftPrecog} from './messagesActions';
import {findMessageById, unsafeIgnoreMessageVersionOptions} from './messagesHelpers';
import {makeGetDraftMessageNamespace} from './messagesSelectors';

/*
 * Compose.
 */

export interface ComposeMessageOptions {
  recipients?: ReadonlyArray<VisualRecipientModel>;
  subject?: string;
  from?: string;
  selectedInboxId?: number;
  channelId?: number;
  trustedHtml?: string;
  text?: string;
  shouldForcePopout?: boolean;
  shouldAttachExternalResources?: boolean;
  attachments?: ReadonlyArray<UploadedAttachmentModel>;
  shouldHideSignature?: boolean;
}

export function createDraftMessageAction(
  options: ComposeMessageOptions = {},
): ThunkAction<DraftMessageModel> {
  return (dispatch, getState) => {
    const state = getState();

    // Find the compose channel.
    const company = getCompany(state);
    const user = getUser(state);

    const getSelectedInboxId = () => options.selectedInboxId;
    const getRecipients = () => options.recipients;
    const getSpecifiedChannelId = () => {
      const {from} = options;
      if (!from) {
        return options.channelId;
      }

      // Try to get the specified channel, if not default to the users channel
      const channels = getUserParticipatingChannels(state);
      return channels.find((c) => c.sendAddress === from.toLowerCase())?.id ?? options.channelId;
    };

    const getComposeChannelOptions = {getSelectedInboxId, getRecipients, getSpecifiedChannelId};
    const composeChannel = makeGetComposeChannel(getComposeChannelOptions)(state, {});

    // We should have a channel at this point.
    if (!composeChannel) {
      throw new Error('A compose channel could not be found.');
    }

    const {contactSource} = composeChannel;
    const recipients = (options.recipients || []).filter((r) => r.contact.source === contactSource);
    const subject = options.subject || '';

    const signature = options.shouldHideSignature ? undefined : getComposeSignature(state, composeChannel);
    const composeRecipients = buildComposeRecipients(composeChannel, recipients);

    // Create the new message.
    const newMessageUid = randomString();
    const draft: DraftMessageModel = {
      id: 0,
      uid: newMessageUid,
      url: buildDraftUrl(newMessageUid, company),
      messageUrl: `${company.url}/messages/${newMessageUid}`,
      conversationId: 0,
      conversationRef: {
        isLastMessage: false,
        isLastManualMessage: false,
        conversationId: 0,
      },
      conversationUrl: '',
      authorId: user.id,
      lastNonVisitorAuthorId: user.id,
      type: composeChannel.messageType,
      visualType: visualTypeFromType(composeChannel.messageType),
      status: MessageStatusesEnum.DRAFT_COMPOSE,
      visualStatus: MessageVisualStatusesEnum.DRAFT,
      subject,
      blurb: '',
      isSeen: false,
      isSenderUnverified: false,
      isReply: false,
      isInbound: false,
      hasMissingBody: false,
      recipients: buildRecipientsFromVisual(composeRecipients),
      attachments: options.attachments || [],
      calendarEventUids: [],
      sentAt: 0,
      updatedAt: 0,
      createdAt: Date.now(),
      fromChannelId: composeChannel.id,
      isShared: false,
      isSaving: false,
      wasSaved: false,
      body: '',
      text: options.text || '',
      html: options.trustedHtml || '',
      hasQuote: false,
      format: options.trustedHtml ? EmailFormatsEnum.HTML : getLastEmailFormat(state),
      signatureId: signature && signature.id,
      isSignatureIncluded: Boolean(signature),
    };

    // Push contacts to the store so we know they exist.
    const contacts = composeRecipients.map((r) => r.contact);

    // Update this message in the store.
    dispatch(updateDataAction({messages: [draft], contacts}));

    return draft;
  };
}

// TODO: Move this to composerPanelActions.
export function composeMessageAction(options: ComposeMessageOptions): ThunkAction<DraftMessageModel> {
  return (dispatch, _getState) => {
    const draft = dispatch(createDraftMessageAction(options));

    // Open a composer.
    dispatch(
      popoutComposerAction(draft.uid, {
        shouldForcePopout: options.shouldForcePopout,
        shouldAttachExternalResources: options.shouldAttachExternalResources,
      }),
    );

    return draft;
  };
}

// TODO: Move this to composerPanelActions.
export function navigateStandaloneComposerAction(
  panelId: ComposerPanelId,
  options?: ComposeMessageOptions,
): ThunkAction {
  return navigateComposerAction(panelId, createDraftMessageAction(options));
}

/*
 * Reply.
 */

interface ReplyToMessageOverrideOptions {
  attachments?: ReadonlyArray<UploadedAttachmentModel>;
  recipients?: ReadonlyArray<VisualRecipientModel>;
  trustedHtml?: string;
  text?: string;
  subject?: string;
  message?: MessageModel;
}

interface ReplyToMessageOptions {
  panelId: ComposerPanelId | ViewerPanelId | MessagePanelId | EmlPanelId;
  conversationId?: number;
  replyToUid: string;
  replyType: ReplyTypesEnum;
  location: ReplyLocationsEnum;
  shouldPopout?: boolean;
  shouldCreateConversation?: boolean;
  shouldIgnoreShowReplyComposerBehavior?: boolean;
  overrides?: ReplyToMessageOverrideOptions;
}

export function replyToMessageAction(options: ReplyToMessageOptions): ThunkAction<DraftMessageModel> {
  return (dispatch, getState) => {
    const {panelId, replyType, shouldCreateConversation, overrides} = options;

    // Track event.
    trackInterfaceEvent(InterfaceEventsEnum.CLICK_REPLY, {
      location: options.location,
      reply_all: replyType === ReplyTypesEnum.ALL,
      reply_type: String(replyType),
      new_conversation: Boolean(shouldCreateConversation),
    });

    // Find the specified conversation.
    const state = getState();
    const localize = getLocalize(state);
    const conversations = getConversations(state);
    const messages = getMessages(state);
    const preferences = getPreferences(state);

    const conversation = options.conversationId && conversations[options.conversationId];

    const draftConversation = (!shouldCreateConversation && conversation) || undefined;

    // Find the message that we want to reply to.
    const message = options.overrides?.message ?? messages[options.replyToUid];
    if (!message || message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      throw new ErrorWithData('Unable to reply to a partial message.', {
        conversation_id: options.conversationId,
        message_uid: options.replyToUid,
        message_visual_status: message?.visualStatus,
        reply_location: options.location,
      });
    }

    // Find the sending channel.
    const getMessage = () => message;
    const replyChannel = makeGetReplyChannel({getMessage})(state, {});
    if (!replyChannel) {
      throw new Error('A reply channel could not be found.');
    }

    // Find the recipients for this message.
    const namespace = (conversation && conversation.namespace) || 'global';
    const replyToRecipients = findMessageRecipients(state, namespace, message);
    const replyRecipients = findReplyRecipients(
      state,
      replyChannel,
      replyType,
      replyToRecipients,
      message.isInbound,
    );

    // Fetch all the card data for variable interpolation
    const recipientForVariableInterpolation = findRecipientForVariableInterpolation(replyRecipients);
    if (recipientForVariableInterpolation) {
      dispatch(fetchCardsForContactAsyncAction(recipientForVariableInterpolation.contact));
    }

    // Check if we already have a draft on this conversation.
    const existingDraft = !shouldCreateConversation && conversation && findDraft(state.data, conversation);

    // Push contacts to the store so we know they exist.
    const contacts = getFinalRecipients(replyRecipients, overrides).map((r) => r.contact);

    const shouldForcePopout = makeGetIsPanelPopup(() => panelId)(state, {}) || isElectron();
    const wasPopoutRequested = options.shouldPopout || preferences.shouldReplyInNewWindow;
    const willPopout =
      wasPopoutRequested &&
      (!replyChannel.behaviors.alwaysShowReplyComposer || options.shouldIgnoreShowReplyComposerBehavior);

    /*
     * Twitter flow.
     */

    // If there's already a draft, nothing to do.
    const isTwitter = message.visualType === MessageVisualTypesEnum.TWEET;
    if (isTwitter && existingDraft) {
      // Focus the composer to be consistent with creating a new draft.
      const composer = findMessageComposer(existingDraft.uid);
      if (composer) {
        composer.focus();
      }
      // Fallback to popout composer if no existing composer.
      else {
        dispatch(popoutComposerAction(existingDraft.uid, {shouldForcePopout}));
      }

      return existingDraft;
    }

    // Otherwise, create it.
    if (isTwitter) {
      const replyText = buildTwitterReplyText(replyRecipients);
      const twitterDraft = buildReplyDraft({
        localize,
        state,
        message,
        channel: replyChannel,
        replyToRecipients: [],
        recipients: [],
        replyType,
        conversation: draftConversation,
        overrides: {
          text: replyText,
        },
      });

      if (willPopout || shouldCreateConversation || !RViewerPanelId.is(panelId)) {
        dispatch(popoutComposerAction(twitterDraft.uid, {shouldForcePopout}));
      } else if (!replyChannel.behaviors.alwaysShowReplyComposer) {
        dispatch(editDraftMessageAction(panelId, twitterDraft.uid));
      }

      dispatch(updateDataAction({messages: [twitterDraft], contacts}));
      return twitterDraft;
    }

    /*
     * Normal flow.
     */

    // If there's already a draft, update it.
    if (existingDraft) {
      if (willPopout || shouldCreateConversation || !RViewerPanelId.is(panelId)) {
        dispatch(popoutComposerAction(existingDraft.uid, {shouldForcePopout}));
      } else if (!replyChannel.behaviors.alwaysShowReplyComposer) {
        dispatch(editDraftMessageAction(panelId, existingDraft.uid));
      }

      const composer = findMessageComposer(existingDraft.uid);
      if (composer) {
        // Use the open composer to save so we don't introduce edit conflicts.
        composer.saveMessage({newRecipients: replyRecipients});
        // Focus the composer to be consistent with creating a new draft.
        composer.focus();

        return existingDraft;
      }

      const patchMessage = buildMessagePatcherFactory()({
        newRecipients: replyRecipients,
      });
      const saveOptions = {
        patchMessage,
        newRecipients: replyRecipients,
        ...unsafeIgnoreMessageVersionOptions,
      };
      dispatch(saveMessageAsyncAction(existingDraft.uid, saveOptions));

      return existingDraft;
    }

    // Otherwise, create it.
    const normalDraft = buildReplyDraft({
      localize,
      state,
      message,
      channel: replyChannel,
      replyToRecipients,
      recipients: replyRecipients,
      replyType,
      overrides,
      conversation: draftConversation,
    });

    batch(() => {
      if (willPopout || shouldCreateConversation || !RViewerPanelId.is(panelId)) {
        dispatch(popoutComposerAction(normalDraft.uid, {shouldForcePopout}));
      } else if (!replyChannel.behaviors.alwaysShowReplyComposer) {
        dispatch(editDraftMessageAction(panelId, normalDraft.uid));
      }

      dispatch(updateDataAction({messages: [normalDraft], contacts}));
    });

    return normalDraft;
  };
}

/*
 * Convert draft to new conversation.
 */

export function forwardDraftInNewConversationAction(
  panelId: ComposerPanelId | ViewerPanelId | MessagePanelId | EmlPanelId,
  draftUid: string,
  location: ReplyLocationsEnum,
): ThunkAction<DraftMessageModel> {
  return (dispatch, getState) => {
    const state = getState();
    const messages = getMessages(state);
    const draft = messages[draftUid];
    if (!draft || draft.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      throw new ErrorWithData('Draft not found', {
        draft_uid: draftUid,
        panel_id: panelId,
        reply_location: location,
      });
    }

    const originalMessage =
      draft.inReplyToId !== undefined ? findMessageById(draft.inReplyToId, messages) : undefined;
    if (!originalMessage) {
      throw new ErrorWithData('Original message not found', {
        draft_uid: draftUid,
        panel_id: panelId,
        reply_location: location,
        original_message_id: draft.inReplyToId,
      });
    }

    if (originalMessage.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      throw new ErrorWithData('Unable to forward when original is a partial message.', {
        draft_uid: draftUid,
        panel_id: panelId,
        reply_location: location,
        original_message_id: originalMessage.id,
      });
    }

    const newDraft = dispatch(
      replyToMessageAction({
        location,
        panelId,
        replyToUid: originalMessage.uid,
        replyType: ReplyTypesEnum.FORWARD,
        shouldCreateConversation: true,
        overrides: {
          attachments: draft.attachments.filter(isUploadedAttachmentModel),
          trustedHtml: draft.html,
          text: draft.text,
          subject: draft.subject,
          message: originalMessage,
        },
      }),
    );
    const saveOptions = {
      patchMessage: buildMessagePatcherFactory()({}),
      ...unsafeIgnoreMessageVersionOptions,
    };
    dispatch(saveMessageAsyncAction(newDraft.uid, saveOptions));
    dispatch(deleteMessageTask(draftUid));
    return newDraft;
  };
}

/*
 * Delete empty drafts.
 */

interface DeleteEmptyDraftsActionOptions {
  conversationId: number;
}

export function deleteEmptyDraftsAction(options: DeleteEmptyDraftsActionOptions): ThunkAction {
  return (dispatch, getState) => {
    // Find the specified conversation.
    const state = getState();
    const conversations = getConversations(state);

    const conversation = conversations[options.conversationId];
    if (!conversation) {
      return;
    }

    // Search all the draft messages
    const getConversation = () => conversation;
    const getDraftMessages = makeGetLoadedDraftMessages(getConversation);
    // This filters out shared drafts that the user is not editing.
    const getUserDraftMessages = makeGetUserDraftComposeMessages(getDraftMessages);
    const drafts = getUserDraftMessages(state, {});
    drafts.forEach((message) => dispatch(deleteMessageTask(message.uid, isEmptyNonEmailDraft)));
  };
}

function isEmptyNonEmailDraft(message: MessageModel): boolean {
  // If we want to support email messages, we'd have to check whether `message.html` is also empty-ish (could be <div><br /></div>). But in practice, we never auto-delete email messages, so 🤷‍♂ .
  return (
    message.visualStatus === MessageVisualStatusesEnum.DRAFT &&
    message.visualType !== MessageVisualTypesEnum.EMAIL &&
    message.status === MessageStatusesEnum.DRAFT_COMPOSE &&
    !message.text.trim() &&
    message.attachments.length === 0
  );
}

function findReplyRecipients(
  state: AppState,
  channel: ChannelModel,
  replyType: ReplyTypesEnum,
  replyToRecipients: ReadonlyArray<VisualRecipientModel>,
  isInbound: boolean,
) {
  const participatingChannels = getUserParticipatingChannels(state);
  const autoBccHandlesForChannelsFromRecipients = makeGetAutoBccHandlesForChannelsFromRecipients<void>(
    () => replyToRecipients,
  )(state);

  // Otherwise let's build the recipients based on the reply type.
  return buildReplyRecipients(
    participatingChannels,
    autoBccHandlesForChannelsFromRecipients,
    channel,
    replyType,
    replyToRecipients,
    isInbound,
  );
}

function findDraft(state: DataState, conversation: ConversationModel) {
  const {userId, messages} = state;
  const {draftMessageUids, unsavedDraftMessageUids = []} = conversation;
  const draftUids = draftMessageUids.concat(unsavedDraftMessageUids);

  return (
    _(draftUids)
      // Find the drafts for the conversation.
      .map((uid) => messages[uid])
      .map((m) => m && m.visualStatus === MessageVisualStatusesEnum.DRAFT && m)
      .compact()
      // Check if there's one for our user.
      .find((message) => {
        const isDraft = message.status === MessageStatusesEnum.DRAFT_COMPOSE;
        const isUserAuthor = message.authorId === userId;
        return isDraft && isUserAuthor;
      })
  );
}

interface BuildReplyOptions {
  localize: Localizer;
  state: AppState;
  conversation?: ConversationModel;
  message: LoadedMessageModel;
  channel: ChannelModel;
  replyToRecipients: ReadonlyArray<VisualRecipientModel>;
  recipients: ReadonlyArray<VisualRecipientModel>;
  replyType: ReplyTypesEnum;
  overrides?: ReplyToMessageOverrideOptions;
}

function buildReplyDraft({
  localize,
  state,
  conversation,
  message,
  channel,
  replyToRecipients,
  recipients,
  replyType,
  overrides,
}: BuildReplyOptions): DraftMessageModel {
  const company = getCompany(state);
  const user = getUser(state);
  const signature = getComposeSignature(state, channel);
  const newMessageUid = randomString();
  const shouldReplyWithOutlookStyle = getShouldReplyWithOutlookStyle(state);
  const hasAutoShareDrafts = getHasAutoShareDrafts(state);
  const shouldBeShared = hasAutoShareDrafts;

  // Building final recipients.
  const finalRecipients = buildRecipientsFromVisual(getFinalRecipients(recipients, overrides));

  // Building final attachments.
  const finalAttachments = getFinalAttachments(message, replyType, overrides);

  // Building final subject.
  const finalSubject = getFinalSubject(localize, replyType, channel, message.subject, overrides);

  return {
    id: 0,
    uid: newMessageUid,
    url: buildDraftUrl(newMessageUid, company, conversation),
    messageUrl: `${company.url}/messages/${newMessageUid}`,
    ...maybeAddConversationProperties(message, conversation),
    authorId: user.id,
    lastNonVisitorAuthorId: user.id,
    type: message.type,
    visualType: message.visualType,
    status: MessageStatusesEnum.DRAFT_COMPOSE,
    visualStatus: MessageVisualStatusesEnum.DRAFT,
    subject: finalSubject,
    blurb: '',
    isSeen: false,
    isSenderUnverified: false,
    isReply: true,
    isInbound: false,
    hasMissingBody: false,
    recipients: finalRecipients,
    attachments: finalAttachments,
    emlAttachments: buildEmlAttachments(message, replyType),
    calendarEventUids: [],
    sentAt: 0,
    updatedAt: 0,
    createdAt: Date.now(),
    fromChannelId: channel.id,
    isShared: shouldBeShared,
    isSaving: false,
    wasSaved: false,
    body: '',
    html: '',
    text: '',
    format: getLastEmailFormat(state),
    ...maybeGetIntroReplyBody(localize, replyType, replyToRecipients, finalRecipients),
    ...maybeGetReplyBodyTextOverrides(overrides),
    ...maybeGetReplyBodyHtmlOverrides(overrides),
    signatureId: signature && signature.id,
    isSignatureIncluded: Boolean(signature),
    ...maybeAddReplyProperties(localize, replyType, message, replyToRecipients, shouldReplyWithOutlookStyle),
    ...maybeAddForwardProperties(localize, replyType, message, replyToRecipients),
  };
}

function maybeGetIntroReplyBody(
  localize: Localizer,
  replyType: ReplyTypesEnum,
  replyToRecipients: ReadonlyArray<VisualRecipientModel>,
  recipients: ReadonlyArray<RecipientModel>,
): Pick<DraftMessageModel, 'html' | 'text'> | {} {
  if (replyType !== ReplyTypesEnum.INTRO) {
    return {};
  }

  const introducer = findIntroducerRecipient(replyToRecipients, recipients);
  if (!introducer) {
    return {};
  }

  const name = getRecipientName(introducer);
  const nameParts: NameParts | undefined = splitName(name);
  const blurb = nameParts
    ? localize(t('reply-intro-body-name')`Thanks, ${nameParts.given}! Moving you to Bcc.`)
    : localize(t('reply-intro-body')`Thanks! Moving you to Bcc.`);
  const text = `${blurb}\n`;
  return {
    text,
    html: textToHtml(text),
  };
}

function maybeGetReplyBodyTextOverrides(overrides: ReplyToMessageOverrideOptions | undefined) {
  if (!overrides || !overrides.text) {
    return {};
  }

  const {text} = overrides;
  return {text};
}

function maybeGetReplyBodyHtmlOverrides(overrides: ReplyToMessageOverrideOptions | undefined) {
  if (!overrides || !overrides.trustedHtml) {
    return {};
  }

  const {trustedHtml} = overrides;
  return {html: trustedHtml, format: EmailFormatsEnum.HTML};
}

function getFinalRecipients(
  recipients: ReadonlyArray<VisualRecipientModel>,
  overrides?: ReplyToMessageOverrideOptions,
) {
  if (overrides && overrides.recipients) {
    return overrides.recipients;
  }

  return recipients;
}

function getFinalAttachments(
  message: MessageModel,
  replyType: ReplyTypesEnum,
  overrides?: ReplyToMessageOverrideOptions,
) {
  if (overrides && overrides.attachments) {
    return overrides.attachments;
  }

  return buildReplyAttachments(message, replyType);
}

function getFinalSubject(
  localize: Localizer,
  replyType: ReplyTypesEnum,
  channel: ChannelModel,
  subject?: string,
  overrides?: ReplyToMessageOverrideOptions,
) {
  if (overrides && isString(overrides.subject)) {
    return overrides.subject;
  }

  if (channel.behaviors.shouldUpdateSubjectOnReply) {
    return buildReplySubject(localize, replyType, subject);
  }

  return subject || '';
}

function buildReplyAttachments(
  message: MessageModel,
  replyType: ReplyTypesEnum,
): ReadonlyArray<AttachmentModel> {
  // Don't include attachments if the message was not an email.
  if (message.type !== MessageTypesEnum.EMAIL) {
    return [];
  }

  // Attachments from EML files are passed to the draft message separately so they can be uploaded.
  if (message.visualStatus === MessageVisualStatusesEnum.EML_FILE) {
    return [];
  }

  // Always use all attachments for forward.
  if (replyType === ReplyTypesEnum.FORWARD) {
    return message.attachments;
  }

  // Only use inline attachments for replies.
  return message.attachments.filter((a) => a.isInline);
}

function buildEmlAttachments(
  message: MessageModel,
  replyType: ReplyTypesEnum,
): ReadonlyArray<AttachmentModel> {
  if (message.visualStatus !== MessageVisualStatusesEnum.EML_FILE) {
    return [];
  }

  if (replyType === ReplyTypesEnum.FORWARD) {
    return message.attachments;
  }

  // We don't need to include inline attachments for replies since EML messages won't have them.
  return [];
}

type DraftMessageConversationProperties = Pick<
  DraftMessageModel,
  'conversationId' | 'conversationRef' | 'conversationUrl' | 'inReplyToId'
>;

function maybeAddConversationProperties(
  message: MessageModel,
  conversation?: ConversationModel,
): DraftMessageConversationProperties {
  // If the draft is in a new conversation
  if (!conversation) {
    return {
      conversationId: 0,
      conversationRef: {
        isLastMessage: false,
        isLastManualMessage: false,
        conversationId: 0,
      },
      conversationUrl: '',
    };
  }

  // If we reply in the conversation
  return {
    conversationId: conversation.id,
    conversationRef: {
      isLastMessage: false,
      isLastManualMessage: false,
      conversationId: conversation.id,
    },
    conversationUrl: conversation.url,
    inReplyToId: message.id,
  };
}

function maybeAddReplyProperties(
  localize: Localizer,
  replyType: ReplyTypesEnum,
  message: LoadedMessageModel,
  replyToRecipients: ReadonlyArray<VisualRecipientModel>,
  shouldReplyWithOutlookStyle: boolean,
) {
  // Find the sender of the message we reply to.
  const recipient = replyToRecipients.find((r) => r.role === RecipientRolesEnum.FROM);
  if (!recipient || replyType === ReplyTypesEnum.FORWARD) {
    return {
      quoteHtml: undefined,
      isQuoteIncluded: false,
      wasQuoteModified: false,
      hasQuote: false,
    };
  }

  const quoteHtml = shouldReplyWithOutlookStyle
    ? buildReplyQuoteWithHeaders(localize, message, replyToRecipients)
    : buildReplyQuote(localize, message, recipient);

  // If one was found, build the quote for the message we reply to.
  return {
    quoteHtml,
    hasQuote: true,
    isQuoteIncluded: true,
    wasQuoteModified: false,
  };
}

function maybeAddForwardProperties(
  localize: Localizer,
  replyType: ReplyTypesEnum,
  message: LoadedMessageModel,
  replyToRecipients: ReadonlyArray<VisualRecipientModel>,
) {
  if (replyType !== ReplyTypesEnum.FORWARD) {
    return {
      forwardHtml: undefined,
      isForwardIncluded: false,
      wasForwardModified: false,
    };
  }

  return {
    forwardHtml: buildForwardQuote(localize, message, replyToRecipients),
    isForwardIncluded: true,
    wasForwardModified: false,
  };
}

function buildDraftUrl(newMessageUid: string, company: CompanyModel, conversation?: ConversationModel) {
  if (conversation) {
    return `${conversation.url}/messages/${newMessageUid}`;
  }

  return `${company.url}/conversations/new/messages/${newMessageUid}`;
}

/*
 * Signature.
 */

function getComposeSignature(state: AppState, composeChannel: ChannelModel) {
  const data = getOptimisticData(state);
  const signatures = selectSignaturesForChannel(data, composeChannel);

  return first(signatures);
}

/*
 * Clone. AKA resend, send again, redirect.
 */

interface CloneMessageOptions {
  panelId: ViewerPanelId | MessagePanelId;
  conversationId: number;
  originalMessageUid: string;
  location: ReplyLocationsEnum;
  shouldPopout?: boolean;
  shouldCreateConversation?: boolean;
}

export function cloneMessage(
  options: CloneMessageOptions,
  parseEmailMessageBody: (body: string, lightBody?: string | undefined) => EmailMessageBodyParts,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const conversations = getConversations(state);
    const messages = getMessages(state);
    const preferences = getPreferences(state);

    const company = getCompany(state);
    const user = getUser(state);

    const conversation = conversations[options.conversationId];
    if (!conversation) {
      return;
    }

    const message = messages[options.originalMessageUid];
    if (!message || message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      return;
    }

    // Same logic as for replying/forwarding.
    const getMessage = () => message;
    const cloneChannel = makeGetReplyChannel({getMessage})(state, {});
    if (!cloneChannel) {
      throw new Error('A clone channel could not be found.');
    }

    const shouldCreateConversation = options.shouldCreateConversation !== false;
    const draftConversation = shouldCreateConversation ? undefined : conversation;

    // For inbound messages, start without any recipients. For outbound messages, clone the recipients.
    const recipients = message.isInbound ? [] : findMessageRecipients(state, conversation.namespace, message);
    const composeRecipients = buildComposeRecipients(cloneChannel, recipients);
    const draftRecipients = buildRecipientsFromVisual(composeRecipients);

    // Try to remove the signature and extract reply/forward quotes.
    const bodyParts = parseEmailMessageBody(message.body, message.lightBody);

    // Same logic as for replying/forwarding.
    const signature = getComposeSignature(state, cloneChannel);

    // Create the new message.
    const newMessageUid = randomString();
    const draft: DraftMessageModel = {
      id: 0,
      uid: newMessageUid,
      url: buildDraftUrl(newMessageUid, company, draftConversation),
      messageUrl: `${company.url}/messages/${newMessageUid}`,
      conversationId: draftConversation ? draftConversation.id : 0,
      conversationRef: {
        isLastMessage: false,
        isLastManualMessage: false,
        conversationId: draftConversation ? draftConversation.id : 0,
      },
      conversationUrl: draftConversation ? draftConversation.url : '',
      authorId: user.id,
      lastNonVisitorAuthorId: user.id,
      type: message.type,
      visualType: message.visualType,
      status: MessageStatusesEnum.DRAFT_COMPOSE,
      visualStatus: MessageVisualStatusesEnum.DRAFT,
      subject: message.subject,
      blurb: '',
      isSeen: false,
      isSenderUnverified: false,
      isReply: message.isReply,
      isInbound: false,
      hasMissingBody: false,
      recipients: draftRecipients,
      attachments: message.attachments,
      calendarEventUids: [],
      sentAt: 0,
      updatedAt: 0,
      createdAt: Date.now(),
      fromChannelId: cloneChannel.id,
      isShared: false,
      isSaving: false,
      wasSaved: false,
      body: '',
      text: '',
      html: sanitizeMessageDraftHtml(bodyParts.bodyHtml, message.id),
      format: EmailFormatsEnum.HTML,
      signatureId: signature && signature.id,
      isSignatureIncluded: Boolean(signature),
      quoteHtml: bodyParts.quoteHtml && sanitizeMessageDraftHtml(bodyParts.quoteHtml, message.id),
      isQuoteIncluded: Boolean(bodyParts.quoteHtml),
      hasQuote: Boolean(bodyParts.quoteHtml),
      wasQuoteModified: false,
      forwardHtml: bodyParts.forwardHtml && sanitizeMessageDraftHtml(bodyParts.forwardHtml, message.id),
      isForwardIncluded: Boolean(bodyParts.forwardHtml),
      wasForwardModified: false,
    };

    // Push contacts to the store so we know they exist.
    const contacts = composeRecipients.map((r) => r.contact);

    // Update this message in the store.
    dispatch(updateDataAction({messages: [draft], contacts}));

    const {panelId} = options;
    const isPanelPopup = makeGetIsPanelPopup(() => panelId)(state, {});
    const wasPopoutRequested = options.shouldPopout || preferences.shouldReplyInNewWindow;
    const shouldPopout = wasPopoutRequested && !cloneChannel.behaviors.alwaysShowReplyComposer;

    if (shouldPopout || shouldCreateConversation || RMessagePanelId.is(panelId)) {
      dispatch(popoutComposerAction(draft.uid, {shouldForcePopout: isPanelPopup || isElectron()}));
    } else if (!cloneChannel.behaviors.alwaysShowReplyComposer) {
      dispatch(editDraftMessageAction(panelId, draft.uid));
    }

    // Track event.
    trackInterfaceEvent(InterfaceEventsEnum.CLICK_CLONE, {
      location: options.location,
      new_conversation: shouldCreateConversation,
    });
  };
}

/*
 * Take ownership/edit shared draft.
 */

export function stealSharedDraft(
  draftMessageUid: string,
  sharedDraftInReplyToUid: string | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const {userId, messages, conversationPrecogs} = state.data;

    // Find our draft in the state.
    const draftMessage = messages[draftMessageUid];
    if (!draftMessage || draftMessage.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      return null;
    }

    // Find the new reply channel.
    const newChannel = findChannelToStealDraft(state, draftMessage);
    if (!newChannel) {
      throw new Error('A reply channel could not be found when taking ownership.');
    }

    // Find the signature to use.
    const signature = getComposeSignature(state, newChannel);

    // If needed, remove the precog for the previous author.
    const previousAuthorPrecog = findDraftPrecog(conversationPrecogs, draftMessage);
    if (previousAuthorPrecog) {
      dispatch(deletePrecog(previousAuthorPrecog));
    }

    // Update the draft.
    const patchMessage = buildMessagePatcherFactory()({
      newAuthorId: userId,
      newChannelId: newChannel.id,
      newSignatureId: signature && signature.id,
      newIsSignatureIncluded: Boolean(signature),
      newWasSignatureModified: false,
      newSignatureHtml: '',
      newRecipients: computeNewRecipientsForStolenDraft(
        state,
        draftMessage,
        newChannel,
        sharedDraftInReplyToUid,
      ),
    });
    return dispatch(
      saveMessageAsyncAction(draftMessage.uid, {patchMessage, ...unsafeIgnoreMessageVersionOptions}),
    );
  };
}

function computeNewRecipientsForStolenDraft(
  state: AppState,
  draftMessage: DraftMessageModel,
  newChannel: ChannelModel,
  sharedDraftInReplyToUid: string | undefined,
) {
  const channels = getChannels(state);
  const fromChannel = channels[draftMessage.fromChannelId];

  // If we don't find a channel, nothing to do.
  if (!fromChannel) {
    return undefined;
  }

  // Grab the handles from the auto-bcc configuration.
  const autoBccHandles = fromChannel.preferences.autoBcc.handles;

  // Build a new collection of recipients from the potential new auto-bcc recipients of the new channel.
  const newAutoBccRecipients: ReadonlyArray<RecipientModel> = (
    (newChannel.preferences.autoBcc.isEnabled && newChannel.preferences.autoBcc.handles) ||
    []
  ).map((handle) => ({
    contactUid: buildContactUid(newChannel.contactSource, handle),
    role: RecipientRolesEnum.BCC,
    handle,
    isSpammer: false,
  }));

  // Try to see if the fromChannel corresponds to a recipient of the message to which the stolen draft replies to.
  const internalFromRecipients = maybeFindInternalFromRecipient(
    state,
    fromChannel,
    newChannel,
    sharedDraftInReplyToUid,
  );

  // Construct the new recipients array.
  const newDraftMessagesRecipients = _(draftMessage.recipients)
    .filter((recipient) => {
      // If the recipient is not a BCC, nothing to do.
      if (recipient.role !== RecipientRolesEnum.BCC) {
        return true;
      }

      if (!recipient.handle) {
        return true;
      }

      // Otherwise, we only want to keep BCCs who are NOT in the list of auto-bcc handles.
      return !autoBccHandles.includes(recipient.handle);
    })
    .concat(newAutoBccRecipients, internalFromRecipients)
    .value();

  // Create a temporary draft message to pass through the selectors.
  const tempNewDraft = {
    ...draftMessage,
    fromChannelId: newChannel.id,
    recipients: newDraftMessagesRecipients,
  };
  // Build an array of visual recipients.
  const getDraftMessageNamespace = makeGetDraftMessageNamespace(() => tempNewDraft);
  const getVisualRecipients = makeGetVisualRecipients(getDraftMessageNamespace, () => tempNewDraft);

  return getVisualRecipients(state, {});
}

function maybeFindInternalFromRecipient(
  state: AppState,
  fromChannel: ChannelModel,
  newChannel: ChannelModel,
  sharedDraftInReplyToUid?: string,
): RecipientModel[] {
  // If we don't have a inReplyTo message uid to begin with, nothing to do.
  if (!sharedDraftInReplyToUid) {
    return [];
  }

  // If we don't have a inReplyTo message to begin with, nothing to do.
  const sharedDraftInReplyTo = getMessages(state)[sharedDraftInReplyToUid];
  if (!sharedDraftInReplyTo) {
    return [];
  }

  const contacts = getContacts(state);

  // For each recipients, try to find those that match the fromChannel and return them
  return sharedDraftInReplyTo.recipients.filter((recipient) => {
    const contact = contacts[recipient.contactUid];
    return contact?.handle === fromChannel.address && contact?.handle !== newChannel.address;
  });
}

function findChannelToStealDraft(state: AppState, message: DraftMessageModel) {
  const getSelectedInboxId = () => undefined;
  const getMessage = () => message;
  const stealChannel = makeGetChannelToStealDraft(getMessage, getSelectedInboxId)(state, {});

  // Temporary instrumentation for https://frontjira.atlassian.net/browse/PB-25327
  if (stealChannel?.id === message.fromChannelId) {
    const shouldReplyWithDefaultChannel = getShouldReplyWithDefaultChannel(state);
    const defaultChannelId = getDefaultChannelId(state);
    const participatingChannels = getUserParticipatingChannels(state);
    const replyChannels = makeGetReplyChannels({getMessage, getSelectedInboxId})(state, {});

    logger.warn('Draft stolen with same channel', {
      message_id: message.id,
      should_reply_with_default_channel: shouldReplyWithDefaultChannel,
      default_channel_id: defaultChannelId,
      participating_channel_ids: participatingChannels.map((c) => c.id),
      reply_channel_ids: replyChannels.map((c) => c.id),
    });
  }

  return stealChannel;
}
